import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from "react";
import ScrollToHash from "./components/ScrollToHash";
import RouteSeo from "./components/RouteSeo";
import { Chatbot } from "./components/Chatbot";
import CookieConsent from "./components/CookieConsent";
import Book from "./pages/Book";
import BookDemo from "./pages/BookDemo";
import BookThankYou from "./pages/BookThankYou";

// Retry dynamic imports so a stale/missing chunk after a redeploy doesn't blank the page.
const lazyWithRetry = (factory: () => Promise<any>) =>
  lazy(async () => {
    const key = "__chunk_reloaded__";
    try {
      return await factory();
    } catch (err) {
      try {
        return await factory();
      } catch (err2) {
        if (typeof window !== "undefined" && !sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
        }
        throw err2;
      }
    }
  });

type RouteErrorBoundaryProps = {
  children: ReactNode;
};

type RouteErrorBoundaryState = {
  hasError: boolean;
};

class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  state: RouteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Route failed to load", error, info);
    // Keep search engines from indexing this temporary recovery screen.
    if (typeof document !== "undefined") {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    }
    // Retry once automatically so visitors and crawlers get the real page.
    const key = "__route_error_reloaded__";
    if (typeof window !== "undefined" && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      window.setTimeout(() => window.location.reload(), 300);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
          <div className="max-w-md text-center">
            <p className="text-3xl md:text-4xl font-normal tracking-tight mb-4" role="status">Loading the latest version</p>
            <p className="text-muted-foreground text-body mb-8">
              The latest site files are loading. Refresh once to continue.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex h-12 items-center justify-center rounded-none bg-primary px-8 text-sm font-semibold text-primary-foreground transition-colours hover:bg-primary/90"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const queryClient = new QueryClient();
const Index = lazyWithRetry(() => import("./pages/Index"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const ContactUs = lazyWithRetry(() => import("./pages/ContactUs"));
const PricingRequest = lazyWithRetry(() => import("./pages/PricingRequest"));
const Product = lazyWithRetry(() => import("./pages/Product"));
const Security = lazyWithRetry(() => import("./pages/Security"));
const TrustCenter = lazyWithRetry(() => import("./pages/TrustCenter"));
const Customers = lazyWithRetry(() => import("./pages/Customers"));
const CaseScenarios = lazyWithRetry(() => import("./pages/CaseScenarios"));
const Careers = lazyWithRetry(() => import("./pages/Careers"));
const JobApplication = lazyWithRetry(() => import("./pages/JobApplication"));
const About = lazyWithRetry(() => import("./pages/About"));
const WhoWeAre = lazyWithRetry(() => import("./pages/about/WhoWeAre"));
const Leadership = lazyWithRetry(() => import("./pages/about/Leadership"));
const Purpose = lazyWithRetry(() => import("./pages/about/Purpose"));
const History = lazyWithRetry(() => import("./pages/about/History"));
const Aspiration = lazyWithRetry(() => import("./pages/about/Aspiration"));
const Governance = lazyWithRetry(() => import("./pages/about/Governance"));
const Team = lazyWithRetry(() => import("./pages/about/Team"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const Healthcare = lazyWithRetry(() => import("./pages/industries/Healthcare"));
const Logistics = lazyWithRetry(() => import("./pages/industries/Logistics"));
const RealEstate = lazyWithRetry(() => import("./pages/industries/RealEstate"));
const LawFirms = lazyWithRetry(() => import("./pages/industries/LawFirms"));
const Ecommerce = lazyWithRetry(() => import("./pages/industries/Ecommerce"));
const Construction = lazyWithRetry(() => import("./pages/industries/Construction"));
const Finance = lazyWithRetry(() => import("./pages/industries/Finance"));
const Hospitality = lazyWithRetry(() => import("./pages/industries/Hospitality"));
const Retail = lazyWithRetry(() => import("./pages/industries/Retail"));
const Education = lazyWithRetry(() => import("./pages/industries/Education"));
const Manufacturing = lazyWithRetry(() => import("./pages/industries/Manufacturing"));
const Insurance = lazyWithRetry(() => import("./pages/industries/Insurance"));
const Travel = lazyWithRetry(() => import("./pages/industries/Travel"));
const Recruitment = lazyWithRetry(() => import("./pages/industries/Recruitment"));
const ItEnterprises = lazyWithRetry(() => import("./pages/industries/ItEnterprises"));
const ClinicGeniusAI = lazyWithRetry(() => import("./pages/ClinicGeniusAI"));
const Clinarium = lazyWithRetry(() => import("./pages/Clinarium"));
const VoiceAgentBuilder = lazyWithRetry(() => import("./pages/VoiceAgentBuilder"));
const RoiCalculatorPage = lazyWithRetry(() => import("./pages/RoiCalculatorPage"));
const Reels = lazyWithRetry(() => import("./pages/Reels"));
const ClinicGeniusThankYou = lazyWithRetry(() => import("./pages/ClinicGeniusThankYou"));
const ProductVoice = lazyWithRetry(() => import("./pages/products/Voice"));
const ProductFlow = lazyWithRetry(() => import("./pages/products/Flow"));
const ProductInsights = lazyWithRetry(() => import("./pages/products/Insights"));
const ProductEngage = lazyWithRetry(() => import("./pages/products/Engage"));
const ProductCustom = lazyWithRetry(() => import("./pages/products/Custom"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const TermsOfUse = lazyWithRetry(() => import("./pages/TermsOfUse"));
const Newsletters = lazyWithRetry(() => import("./pages/Newsletters"));
const IndustryDepth = lazyWithRetry(() => import("./pages/how-we-help/IndustryDepth"));
const AdvisoryBuild = lazyWithRetry(() => import("./pages/how-we-help/AdvisoryBuild"));
const AIEmbedded = lazyWithRetry(() => import("./pages/how-we-help/AIEmbedded"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPostSimpleVsAgenticAI = lazyWithRetry(() => import("./pages/BlogPostSimpleVsAgenticAI"));
const BlogPostHealthcareAI = lazyWithRetry(() => import("./pages/BlogPostHealthcareAI"));
const BlogPostLawFirmsAI = lazyWithRetry(() => import("./pages/BlogPostLawFirmsAI"));
const BlogPostRealEstateAI = lazyWithRetry(() => import("./pages/BlogPostRealEstateAI"));
const BlogPostRetailAI = lazyWithRetry(() => import("./pages/BlogPostRetailAI"));
const BlogPostEducationAI = lazyWithRetry(() => import("./pages/BlogPostEducationAI"));
const BlogPostHospitalityAI = lazyWithRetry(() => import("./pages/BlogPostHospitalityAI"));
const BlogPostConstructionAI = lazyWithRetry(() => import("./pages/BlogPostConstructionAI"));
const BlogPostWorkflowHealthcare = lazyWithRetry(() => import("./pages/BlogPostWorkflowHealthcare"));
const BlogPostEcommerceDiscovery = lazyWithRetry(() => import("./pages/BlogPostEcommerceDiscovery"));
const BlogPostPersonalisedCare = lazyWithRetry(() => import("./pages/BlogPostPersonalisedCare"));
const BlogPostManufacturingAI = lazyWithRetry(() => import("./pages/BlogPostManufacturingAI"));
const BlogPostCliniciansDrowningAdmin = lazyWithRetry(() => import("./pages/BlogPostCliniciansDrowningAdmin"));
const BlogPostTravelPersonalisation = lazyWithRetry(() => import("./pages/BlogPostTravelPersonalisation"));
const BlogPostInsuranceAI = lazyWithRetry(() => import("./pages/BlogPostInsuranceAI"));
const BlogPostBestLawyersAdmin = lazyWithRetry(() => import("./pages/BlogPostBestLawyersAdmin"));
const BlogPostAiReceptionistMissedCalls = lazyWithRetry(() => import("./pages/BlogPostAiReceptionistMissedCalls"));
const BlogPostQuantitySurveyingAI = lazyWithRetry(() => import("./pages/BlogPostQuantitySurveyingAI"));
const BlogPostNewPatientJourney = lazyWithRetry(() => import("./pages/BlogPostNewPatientJourney"));
const BlogPostTeacherRetentionAdmin = lazyWithRetry(() => import("./pages/BlogPostTeacherRetentionAdmin"));
const BlogPostClinicalDocumentation = lazyWithRetry(() => import("./pages/BlogPostClinicalDocumentation"));
const BlogPostNoShowsSystem = lazyWithRetry(() => import("./pages/BlogPostNoShowsSystem"));
const BlogPostPrivatePracticeGrowth = lazyWithRetry(() => import("./pages/BlogPostPrivatePracticeGrowth"));
const BlogPostClinicianBurnout = lazyWithRetry(() => import("./pages/BlogPostClinicianBurnout"));
const BlogPostDiagnosticDelay = lazyWithRetry(() => import("./pages/BlogPostDiagnosticDelay"));
const BlogPostDataFragmentation = lazyWithRetry(() => import("./pages/BlogPostDataFragmentation"));
const BlogPostElderlyCareCrisis = lazyWithRetry(() => import("./pages/BlogPostElderlyCareCrisis"));
const BlogPostChronicDisease = lazyWithRetry(() => import("./pages/BlogPostChronicDisease"));
const BlogPostMedicationAdherence = lazyWithRetry(() => import("./pages/BlogPostMedicationAdherence"));
const BlogPostHealthInequality = lazyWithRetry(() => import("./pages/BlogPostHealthInequality"));
const BlogPostPreventionPopulationHealth = lazyWithRetry(() => import("./pages/BlogPostPreventionPopulationHealth"));
const BlogPostMentalHealthCrisis = lazyWithRetry(() => import("./pages/BlogPostMentalHealthCrisis"));
const BlogPostNhsWaitingList = lazyWithRetry(() => import("./pages/BlogPostNhsWaitingList"));
const BlogPostWearableMonitoring = lazyWithRetry(() => import("./pages/BlogPostWearableMonitoring"));
const BlogPostGenerativeAiClinic = lazyWithRetry(() => import("./pages/BlogPostGenerativeAiClinic"));
const BlogPostPracticeCapacityUtilisation = lazyWithRetry(() => import("./pages/BlogPostPracticeCapacityUtilisation"));
const BlogPostClinicalDataEntryErrors = lazyWithRetry(() => import("./pages/BlogPostClinicalDataEntryErrors"));
const BlogPostReferralManagement = lazyWithRetry(() => import("./pages/BlogPostReferralManagement"));
const BlogPostUnansweredPatientMessages = lazyWithRetry(() => import("./pages/BlogPostUnansweredPatientMessages"));
const BlogPostProjectsFail = lazyWithRetry(() => import("./pages/BlogPostProjectsFail"));
const BlogPostInsuranceAdmin = lazyWithRetry(() => import("./pages/BlogPostInsuranceAdmin"));
const BlogPostAutomationCapabilityLine = lazyWithRetry(() => import("./pages/BlogPostAutomationCapabilityLine"));
const BlogPostFirstNinetyDays = lazyWithRetry(() => import("./pages/BlogPostFirstNinetyDays"));
const BlogPostCqcCompliance = lazyWithRetry(() => import("./pages/BlogPostCqcCompliance"));
const BlogPostSmallBusinessAdvantage = lazyWithRetry(() => import("./pages/BlogPostSmallBusinessAdvantage"));
const BlogPostSoloPracticeCost = lazyWithRetry(() => import("./pages/BlogPostSoloPracticeCost"));
const BlogPostPracticeInboxTriage = lazyWithRetry(() => import("./pages/BlogPostPracticeInboxTriage"));
const BlogPostPracticeManager = lazyWithRetry(() => import("./pages/BlogPostPracticeManager"));
const BlogPostEnquiryConversion = lazyWithRetry(() => import("./pages/BlogPostEnquiryConversion"));
const BlogPostFounderBlocker = lazyWithRetry(() => import("./pages/BlogPostFounderBlocker"));
const BlogPostHonestyDeal = lazyWithRetry(() => import("./pages/BlogPostHonestyDeal"));
const BlogPostYoungerBrother = lazyWithRetry(() => import("./pages/BlogPostYoungerBrother"));
const BlogPostFifteenPersonBusiness = lazyWithRetry(() => import("./pages/BlogPostFifteenPersonBusiness"));
const BlogPostTrustUnderstanding = lazyWithRetry(() => import("./pages/BlogPostTrustUnderstanding"));
const BlogPostSlackEnterprisePilot = lazyWithRetry(() => import("./pages/BlogPostSlackEnterprisePilot"));
const BlogPostReceptionistTrained = lazyWithRetry(() => import("./pages/BlogPostReceptionistTrained"));
const BlogPostReceptionDesks = lazyWithRetry(() => import("./pages/BlogPostReceptionDesks"));
const BlogPost740pmCall = lazyWithRetry(() => import("./pages/BlogPost740pmCall"));
const BlogPostDataReadiness = lazyWithRetry(() => import("./pages/BlogPostDataReadiness"));
const BlogPostDiscoveryNinetySeconds = lazyWithRetry(() => import("./pages/BlogPostDiscoveryNinetySeconds"));
const CaseStudyDentalNoShowRecovery = lazyWithRetry(() => import("./pages/CaseStudyDentalNoShowRecovery"));
const CaseStudyCandidateCommunication = lazyWithRetry(() => import("./pages/CaseStudyCandidateCommunication"));
const CaseScenarioPhysiotherapyVisibility = lazyWithRetry(() => import("./pages/CaseScenarioPhysiotherapyVisibility"));
const CaseScenarioFinancialOnboarding = lazyWithRetry(() => import("./pages/CaseScenarioFinancialOnboarding"));
const CaseScenarioEcommerceReturns = lazyWithRetry(() => import("./pages/CaseScenarioEcommerceReturns"));
const CaseScenarioHotelDirectBookings = lazyWithRetry(() => import("./pages/CaseScenarioHotelDirectBookings"));
const CaseScenarioLettingsResponse = lazyWithRetry(() => import("./pages/CaseScenarioLettingsResponse"));
const CaseScenarioSupplierQueries = lazyWithRetry(() => import("./pages/CaseScenarioSupplierQueries"));



const TestimonialsPage = lazyWithRetry(() => import("./pages/Testimonials"));

const withPage = (page: JSX.Element) => (
  <RouteErrorBoundary>
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <main id="main-content">{page}</main>
    </Suspense>
  </RouteErrorBoundary>
);

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={withPage(<Index />)} />
        <Route path="/product" element={withPage(<Product />)} />
        <Route path="/products/voice" element={withPage(<ProductVoice />)} />
        <Route path="/products/flow" element={withPage(<ProductFlow />)} />
        <Route path="/products/insights" element={withPage(<ProductInsights />)} />
        <Route path="/products/engage" element={withPage(<ProductEngage />)} />
        <Route path="/products/custom" element={withPage(<ProductCustom />)} />
        <Route path="/security" element={withPage(<Security />)} />
        <Route path="/trust-center" element={withPage(<TrustCenter />)} />
        <Route path="/case-studies" element={withPage(<Customers />)} />
        <Route path="/case-scenarios" element={withPage(<CaseScenarios />)} />
        <Route path="/customers" element={<Navigate to="/case-studies" replace />} />
        <Route path="/testimonials" element={withPage(<TestimonialsPage />)} />
        <Route path="/careers" element={withPage(<Careers />)} />
        <Route path="/careers/apply" element={withPage(<JobApplication />)} />
        <Route path="/about" element={withPage(<About />)} />
        <Route path="/about/who-we-are" element={withPage(<WhoWeAre />)} />
        <Route path="/about/leadership" element={withPage(<Leadership />)} />
        <Route path="/about/purpose" element={withPage(<Purpose />)} />
        <Route path="/about/history" element={withPage(<History />)} />
        <Route path="/about/aspiration" element={withPage(<Aspiration />)} />
        <Route path="/about/governance" element={withPage(<Governance />)} />
        <Route path="/about/team" element={withPage(<Team />)} />
        <Route path="/contact-us" element={withPage(<ContactUs />)} />
        <Route path="/contact" element={withPage(<Contact />)} />
        <Route path="/book" element={withPage(<Book />)} />
        <Route path="/book-a-demo" element={withPage(<BookDemo />)} />
        <Route path="/booking-confirmed" element={withPage(<BookThankYou />)} />

        <Route path="/pricing-request" element={withPage(<PricingRequest />)} />
        <Route path="/industries/healthcare" element={withPage(<Healthcare />)} />
        <Route path="/industries/logistics" element={withPage(<Logistics />)} />
        <Route path="/industries/real-estate" element={withPage(<RealEstate />)} />
        <Route path="/industries/law-firms" element={withPage(<LawFirms />)} />
        <Route path="/industries/ecommerce" element={withPage(<Ecommerce />)} />
        <Route path="/industries/construction" element={withPage(<Construction />)} />
        <Route path="/industries/finance" element={withPage(<Finance />)} />
        <Route path="/industries/hospitality" element={withPage(<Hospitality />)} />
        <Route path="/industries/retail" element={withPage(<Retail />)} />
        <Route path="/industries/education" element={withPage(<Education />)} />
        <Route path="/industries/manufacturing" element={withPage(<Manufacturing />)} />
        <Route path="/industries/insurance" element={withPage(<Insurance />)} />
        <Route path="/industries/travel-tourism" element={withPage(<Travel />)} />
        <Route path="/industries/recruitment" element={withPage(<Recruitment />)} />
        <Route path="/industries/it-enterprises" element={withPage(<ItEnterprises />)} />
        <Route path="/clinic-genius-ai" element={withPage(<ClinicGeniusAI />)} />
        <Route path="/clinarium" element={withPage(<Clinarium />)} />
        <Route path="/voice-agent-builder" element={withPage(<VoiceAgentBuilder />)} />
        <Route path="/roi-calculator/:industry?" element={withPage(<RoiCalculatorPage />)} />
        <Route path="/reels" element={withPage(<Reels />)} />
        <Route path="/clinic-genius-thank-you" element={withPage(<ClinicGeniusThankYou />)} />
        <Route path="/privacy-policy" element={withPage(<PrivacyPolicy />)} />
        <Route path="/unsubscribe" element={withPage(<Unsubscribe />)} />
        <Route path="/email-unsubscribe" element={withPage(<Unsubscribe />)} />
        <Route path="/terms-of-use" element={withPage(<TermsOfUse />)} />
        <Route path="/newsletters" element={withPage(<Newsletters />)} />
        <Route path="/how-we-help/industry-depth" element={withPage(<IndustryDepth />)} />
        <Route path="/how-we-help/advisory-build" element={withPage(<AdvisoryBuild />)} />
        <Route path="/how-we-help/ai-embedded" element={withPage(<AIEmbedded />)} />
        <Route path="/blog" element={withPage(<Blog />)} />
        <Route path="/blog/simple-vs-agentic-automation" element={withPage(<BlogPostSimpleVsAgenticAI />)} />
        <Route path="/blog/private-practice-management" element={withPage(<BlogPostHealthcareAI />)} />
        <Route path="/blog/small-law-firm-efficiency" element={withPage(<BlogPostLawFirmsAI />)} />
        <Route path="/blog/estate-agent-productivity" element={withPage(<BlogPostRealEstateAI />)} />
        <Route path="/blog/retail-personalisation" element={withPage(<BlogPostRetailAI />)} />
        <Route path="/blog/personalised-learning-education" element={withPage(<BlogPostEducationAI />)} />
        <Route path="/blog/personalised-guest-experience-hospitality" element={withPage(<BlogPostHospitalityAI />)} />
        <Route path="/blog/predictive-construction-delays" element={withPage(<BlogPostConstructionAI />)} />
        <Route path="/blog/workflow-automation-healthcare-operations" element={withPage(<BlogPostWorkflowHealthcare />)} />
        <Route path="/blog/ecommerce-product-discovery" element={withPage(<BlogPostEcommerceDiscovery />)} />
        <Route path="/blog/personalised-care-precision-medicine" element={withPage(<BlogPostPersonalisedCare />)} />
        <Route path="/blog/factory-of-the-future-intelligent-manufacturing" element={withPage(<BlogPostManufacturingAI />)} />
        <Route path="/blog/great-clinicians-drowning-in-admin" element={withPage(<BlogPostCliniciansDrowningAdmin />)} />
        <Route path="/blog/personalised-travel-experiences" element={withPage(<BlogPostTravelPersonalisation />)} />
        <Route path="/blog/insurance-claims-smarter-risk" element={withPage(<BlogPostInsuranceAI />)} />
        <Route path="/blog/best-lawyers-least-time-practising-law" element={withPage(<BlogPostBestLawyersAdmin />)} />
        <Route path="/blog/reception-management-missed-calls-healthcare" element={withPage(<BlogPostAiReceptionistMissedCalls />)} />
        <Route path="/blog/quantity-surveying-data" element={withPage(<BlogPostQuantitySurveyingAI />)} />
        <Route path="/blog/new-patient-journey-first-impressions" element={withPage(<BlogPostNewPatientJourney />)} />
        <Route path="/blog/teacher-retention-admin-burden" element={withPage(<BlogPostTeacherRetentionAdmin />)} />
        <Route path="/blog/clinical-documentation-burden" element={withPage(<BlogPostClinicalDocumentation />)} />
        <Route path="/blog/no-shows-fixable-system-problem" element={withPage(<BlogPostNoShowsSystem />)} />
        <Route path="/blog/private-practice-growth-without-headcount" element={withPage(<BlogPostPrivatePracticeGrowth />)} />
        <Route path="/blog/clinician-burnout-system-problem" element={withPage(<BlogPostClinicianBurnout />)} />
        <Route path="/blog/diagnostic-delay-pattern-recognition" element={withPage(<BlogPostDiagnosticDelay />)} />
        <Route path="/blog/patient-data-fragmentation" element={withPage(<BlogPostDataFragmentation />)} />
        <Route path="/blog/elderly-care-crisis-intervention" element={withPage(<BlogPostElderlyCareCrisis />)} />
        <Route path="/blog/chronic-disease-continuous-monitoring" element={withPage(<BlogPostChronicDisease />)} />
        <Route path="/blog/medication-adherence-non-adherence-nhs" element={withPage(<BlogPostMedicationAdherence />)} />
        <Route path="/blog/health-inequality-access-gap" element={withPage(<BlogPostHealthInequality />)} />
        <Route path="/blog/prevention-population-health-nhs" element={withPage(<BlogPostPreventionPopulationHealth />)} />
        <Route path="/blog/mental-health-waiting-times-digital-support" element={withPage(<BlogPostMentalHealthCrisis />)} />
        <Route path="/blog/nhs-waiting-list-patient-pathway-management" element={withPage(<BlogPostNhsWaitingList />)} />
        <Route path="/blog/wearable-health-data-remote-monitoring" element={withPage(<BlogPostWearableMonitoring />)} />
        <Route path="/blog/ambient-scribing-clinical-documentation-nhs" element={withPage(<BlogPostGenerativeAiClinic />)} />
        <Route path="/blog/private-practice-capacity-utilisation-uk" element={withPage(<BlogPostPracticeCapacityUtilisation />)} />
        <Route path="/blog/clinical-data-entry-errors-document-automation" element={withPage(<BlogPostClinicalDataEntryErrors />)} />
        <Route path="/blog/referral-management-patient-pathway" element={withPage(<BlogPostReferralManagement />)} />
        <Route path="/blog/unanswered-patient-messages" element={withPage(<BlogPostUnansweredPatientMessages />)} />
        <Route path="/blog/why-projects-fail-four-questions" element={withPage(<BlogPostProjectsFail />)} />
        <Route path="/blog/insurance-claims-administration-private-practice" element={withPage(<BlogPostInsuranceAdmin />)} />
        <Route path="/blog/first-90-days-automation-sequence" element={withPage(<BlogPostFirstNinetyDays />)} />
        <Route path="/blog/business-automation-capability-line" element={withPage(<BlogPostAutomationCapabilityLine />)} />
        <Route path="/blog/cqc-compliance-continuous-private-practice" element={withPage(<BlogPostCqcCompliance />)} />
        <Route path="/blog/small-business-advantage-automation-wave" element={withPage(<BlogPostSmallBusinessAdvantage />)} />
        <Route path="/case-studies/single-handed-practice-real-cost" element={withPage(<BlogPostSoloPracticeCost />)} />
        <Route path="/blog/single-handed-practice-real-cost" element={<Navigate to="/case-studies/single-handed-practice-real-cost" replace />} />
        <Route path="/case-studies/practice-manager-would-not-let-go" element={withPage(<BlogPostPracticeManager />)} />
        <Route path="/case-studies/22-to-58-enquiry-conversion" element={withPage(<BlogPostEnquiryConversion />)} />
        <Route path="/case-studies/dental-practice-no-show-recovery" element={withPage(<CaseStudyDentalNoShowRecovery />)} />
        <Route path="/case-scenarios/candidate-communication-automation" element={withPage(<CaseStudyCandidateCommunication />)} />
        <Route path="/case-scenarios/physiotherapy-group-performance-visibility" element={withPage(<CaseScenarioPhysiotherapyVisibility />)} />
        <Route path="/case-scenarios/enterprise-financial-services-onboarding" element={withPage(<CaseScenarioFinancialOnboarding />)} />
        <Route path="/case-scenarios/ecommerce-returns-processing" element={withPage(<CaseScenarioEcommerceReturns />)} />
        <Route path="/case-scenarios/boutique-hotel-direct-bookings" element={withPage(<CaseScenarioHotelDirectBookings />)} />
        <Route path="/case-scenarios/lettings-agency-enquiry-response" element={withPage(<CaseScenarioLettingsResponse />)} />
        <Route path="/case-scenarios/construction-supplier-query-response" element={withPage(<CaseScenarioSupplierQueries />)} />
        <Route path="/case-studies/candidate-communication-automation" element={<Navigate to="/case-scenarios/candidate-communication-automation" replace />} />
        <Route path="/blog/practice-inbox-triage-system" element={withPage(<BlogPostPracticeInboxTriage />)} />
        <Route path="/blog/founder-biggest-blocker-automation" element={withPage(<BlogPostFounderBlocker />)} />
        <Route path="/blog/honesty-almost-cost-us-the-deal" element={withPage(<BlogPostHonestyDeal />)} />
        <Route path="/blog/15-person-business-enterprise-lesson" element={withPage(<BlogPostFifteenPersonBusiness />)} />
        <Route path="/blog/trust-not-understanding" element={withPage(<BlogPostTrustUnderstanding />)} />
        <Route path="/blog/slack-message-enterprise-pilot" element={withPage(<BlogPostSlackEnterprisePilot />)} />
        <Route path="/blog/receptionist-trained-our-system" element={withPage(<BlogPostReceptionistTrained />)} />
        <Route path="/blog/what-reception-desks-taught-us" element={withPage(<BlogPostReceptionDesks />)} />
        <Route path="/blog/call-at-740pm-after-hours" element={withPage(<BlogPost740pmCall />)} />
        <Route path="/blog/data-not-as-bad-as-you-think" element={withPage(<BlogPostDataReadiness />)} />
        <Route path="/blog/first-ninety-seconds-discovery-call" element={withPage(<BlogPostDiscoveryNinetySeconds />)} />
        <Route path="/blog/younger-brother-family-practice" element={withPage(<BlogPostYoungerBrother />)} />


        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={withPage(<NotFound />)} />
      </Routes>
    </AnimatePresence>
  );
};

const SiteOverlays = () => {
  const { pathname } = useLocation();
  const isBookingPage = pathname === "/book" || pathname === "/book-a-demo";

  if (isBookingPage) return null;

  return (
    <>
      <Chatbot />
      <CookieConsent />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToHash />
        <RouteSeo />
        <AnimatedRoutes />
        <SiteOverlays />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
